<template>
  <div class="d-flex">
    <div class="map-locations-container">
      <v-list two-line>
        <v-list-item
          v-for="(routeLocation, index) in routeLocations" :key="index"
          class="pl-0">
          <v-list-item-content v-if="routeLocation.location.isDc()">
            <vf-tooltip :text="`${$t('dc')} - ${routeLocation.location.label}`">
              <v-list-item-title>
                {{ $t('dc') }} - {{ routeLocation.location.label }}
              </v-list-item-title>
            </vf-tooltip>
            <v-list-item-subtitle>
              <div>
                {{ $t('departure') }} -
                <multi-time-display v-if="routeLocation.vehicle_departure" :time="routeLocation.vehicle_departure" />
              </div>
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-content v-else>
            <vf-tooltip :text="`${$tc('stop')} ${index} - ${routeLocation.location.label}`">
              <v-list-item-title>
                {{ $tc('stop') }} {{ index }} - {{ routeLocation.location.label }}
              </v-list-item-title>
            </vf-tooltip>
            <v-list-item-subtitle>
              <div>
                {{ $t('arrival') }} -
                <multi-time-display v-if="routeLocation.vehicle_arrival" :time="routeLocation.vehicle_arrival" />
              </div>
              <div>
                {{ $t('departure') }} -
                <multi-time-display v-if="routeLocation.vehicle_departure" :time="routeLocation.vehicle_departure" />
              </div>
              <div>
                {{ $t('duration') }} -
                <span v-if="!routeLocation.unregistered_location">
                {{ routeLocation.getVehicleStopDuration() | humanizeDuration(({ numberOfSignificantParts: 2, separator: ', ' })) }}
              </span>
              </div>
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-btn icon @click="locationHandlers[index].click($event)">
              <v-icon color="primary">mdi-crosshairs-gps</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </div>
    <div class="map-container">
      <gmap-map class="route-plan-map" ref="map" :zoom="12" :center="dummyCenter"/>
      <p class="path-disclaimer mt-3 mb-0">{{ $t('vehicle_path_disclaimer') }}</p>
      <div class="d-flex">
        <v-switch v-model="showVehiclePath" :label="$t('show_vehicle_path')"/>
        <v-switch v-model="showGpsMarkers" :label="$t('show_gps_data')" class="ml-4"/>
      </div>
    </div>
  </div>
</template>

<script>
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import VfTooltip from '@/components/shared/VfTooltip'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import TemperatureMixin from '@/components/mixins/TemperatureMixin'
import Moment from 'moment-timezone'
import { gmapApi } from 'vue2-google-maps'
import { forEach, size } from 'lodash'

export default {
  name: 'RouteMap',
  components: {
    MultiTimeDisplay,
    VfTooltip
  },
  mixins: [
    CurrentUserMixin,
    DateTimeUtilsMixin,
    TemperatureMixin
  ],
  props: {
    /**
     * The route to display
     */
    route: Object,
    /**
     * A list of the route locations to display (This prop assumes some preparation was done externally to flag
     * unregistered locations, etc
     */
    routeLocations: Array
  },
  data () {
    return {
      // local reference to google map, will be set when map promise resolves.
      map: undefined,
      // vue-google-maps complains if no center is defined,
      // however we can't calculate the bounds until the map promise resolves and window.google is available.
      dummyCenter: { lat: 0, lng: 0 },
      // A collection of functions for each location in the locations table. Indexed by order.
      locationHandlers: [],
      // A reference to the google maps PolyLine that shows the vehicle's path.
      vehiclePath: null,
      // A reference to the collected gps markers
      gpsMarkers: [],
      showVehiclePath: true,
      showGpsMarkers: true
    }
  },
  methods: {
    getLocationColor (routeLocation) {
      if (routeLocation.missed_stop) {
        return 'danger'
      }
      if (routeLocation.unplanned_stop) {
        return 'warning'
      }
      return 'primary'
    },
    /**
     * Get the tooltip to display, given a route location.
     * @param routeLocation
     * @return String the tooltip to display
     */
    getLocationTooltip (routeLocation) {
      if (routeLocation.missed_stop) {
        return this.$tc('missed_stop')
      }
      if (routeLocation.unplanned_stop) {
        return this.$tc('unplanned_stop')
      }
      if (routeLocation.unregistered_location) {
        return this.$tc('unregistered_location')
      }
      return this.$t(routeLocation.location.type + '_location_type')
    },
    /**
     * Set up the initial google map state.
     *
     * The functionality here is pretty much directly ported over from the original Yii implementation,
     * which used jquery. As a result, there is perhaps too much closure state for a Vue component.
     * This state is used by event handlers that are exposed via the locationHandlers array.
     *
     * It's possible this could be refactored to be much more vue-like. However, a lot of this is using the
     * raw google maps API, which isn't available until the maps promise resolves
     * (see the mounted code that invokes this method), so that might be tricky.
     */
    initializeMap () {
      const bounds = new this.google.maps.LatLngBounds()
      const map = this.map
      const infowindow = new this.google.maps.InfoWindow()

      forEach(this.routeLocations, (routeLocation, index) => {
        // For unregistered locations take the user to the location create page
        // if they click the location in the route locations table
        if (routeLocation.unregistered_location) {
          this.locationHandlers.push({
            click: () => {
              window.location.href = `/location/create?unregistered=1&code=${routeLocation.location.code}`
            }
          })
          // For unregsitered locations we're done (the code below deals with
          // rendering the location on the map which we can't do since we don't
          // have GPS coordinates). We're in a forEach function, not an each() so
          // return is the equivelent of continue in this context
          return
        }

        const location = routeLocation.location

        const latlng = new this.google.maps.LatLng(location.latitude, location.longitude)
        bounds.extend(latlng)

        if (size(location.googleMapsPolygon) > 0) {
          /* eslint-disable-next-line no-new */
          new this.google.maps.Polygon({
            map: map,
            paths: location.googleMapsPolygon,
            strokeColor: '#337ab7',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#337ab7',
            fillOpacity: 0.35
          })
        }

        // Create the marker for this location
        const markerOptions = {
          position: latlng,
          title: location.label,
          info: this.createInfoWindow(routeLocation),
          map: map,
          zIndex: 100,
          label: index.toString()
        }

        if (location.type === 'dc') {
          markerOptions.label = ''
          markerOptions.icon = {
            scaledSize: new this.google.maps.Size(30, 30),
            url: '/img/building-solid.svg',
            color: '#337ab7'
          }
        } else if (routeLocation.missed_stop) {
          markerOptions.icon = {
            url: '/img/map-marker-solid-red.svg',
            scaledSize: new this.google.maps.Size(30, 30),
            labelOrigin: new this.google.maps.Point(15, 12)
          }
        } else if (routeLocation.unplanned_stop) {
          markerOptions.icon = {
            url: '/img/map-marker-solid-info.svg',
            scaledSize: new this.google.maps.Size(30, 30),
            labelOrigin: new this.google.maps.Point(15, 12)
          }
        } else {
          markerOptions.icon = {
            url: '/img/map-marker-solid-blue.svg',
            scaledSize: new this.google.maps.Size(30, 30),
            labelOrigin: new this.google.maps.Point(15, 12)
          }
        }
        const marker = new this.google.maps.Marker(markerOptions)

        // Displays the InfoWindow when the marker is clicked
        this.google.maps.event.addListener(marker, 'click', (function (marker, index) {
          return function () {
            infowindow.setContent(marker.info)
            infowindow.open(map, marker)
          }
        })(marker, index))

        // Create handler methods bound to this location,
        // indexed by their position in the table.
        const infoWindowHandler = (e) => {
          infowindow.setContent(marker.info)
          infowindow.open(map, marker)
          map.setCenter(marker.getPosition())
          return false
        }
        this.locationHandlers.push({
          click: infoWindowHandler
        })
      })

      this.vehiclePath = new this.google.maps.Polyline({
        strokeColor: '#2c5699',
        strokeOpacity: 1,
        strokeWeight: 3,
        map: map
      })
      const path = this.vehiclePath.getPath()

      forEach(this.route.interpolated_sensor_data, (value, key) => {
        if (value.lat && value.long) {
          const latlng = new this.google.maps.LatLng(value.lat, value.long)
          path.push(latlng)

          const marker = new this.google.maps.Marker({
            position: latlng,
            icon: {
              url: '/img/map-marker-circle-red.png',
              scaledSize: new this.google.maps.Size(12, 12),
              anchor: new this.google.maps.Point(6, 6),
              zIndex: 50
            },
            info: `<h6>${Moment(key).tz(this.customerContext.timezone()).format('lll zz')}</h6><span>${value.lat}, ${value.long}</span>`,
            map: map
          })

          this.gpsMarkers.push(marker)

          // Displays the InfoWindow when the marker is clicked
          this.google.maps.event.addListener(marker, 'click', (function (marker) {
            return function () {
              infowindow.setContent(marker.info)
              infowindow.open(map, marker)
            }
          })(marker))
        }
      })

      // Intentionally left as route.route_locations rather than routeLocations
      // because in this case we can only use known locations (with GPS
      // coordinates)
      if (size(this.route.route_locations) === 1) {
        const location = this.route.route_locations[0].location
        const latlng = new this.google.maps.LatLng(location.latitude, location.longitude)
        map.panTo(latlng)
        map.setZoom(15)
      } else {
        map.fitBounds(bounds)
        map.panToBounds(bounds)
      }
    },
    /**
     * Given a route location, create a Google Maps info window that will be displayed on the map.
     * @param routeLocation
     * @return InfoWindow
     */
    createInfoWindow (routeLocation) {
      const location = routeLocation.location
      let infoWindow = `<h5>${location.label}</h5>`
      let lookupTime = null

      let timestamp = null
      if (location.type === 'dc' && routeLocation.vehicle_departure) {
        timestamp = routeLocation.vehicle_departure
        lookupTime = routeLocation.vehicle_departure
      } else if (routeLocation.vehicle_arrival) {
        timestamp = routeLocation.vehicle_arrival
        lookupTime = routeLocation.vehicle_arrival
      } else if (routeLocation.eta) {
        timestamp = routeLocation.eta
      }

      infoWindow += `<h6>${Moment(timestamp).tz(this.customerContext.timezone()).format('lll zz')}</h6>`
      if (lookupTime) {
        // Zero out to the minute and reformat so we can look up product data
        lookupTime = Moment.utc(lookupTime).seconds(0).format()

        if (this.route.interpolated_sensor_data[lookupTime]) {
          forEach(this.route.route_products, (routeProduct) => {
            const productName = routeProduct.product.name
            // Sensor data is keyed by the stable product_{id} (VF-4177)
            const temperature = this.route.interpolated_sensor_data[lookupTime][`product_${routeProduct.product_id}`]
            infoWindow += `<div>${productName}: ${this.temperatureToCustomerScale(temperature)} &deg;${this.customerContext.tempFormat()}</div>`
          })
        }
      }
      return infoWindow
    },
    /**
     * Invoke appropriate row hover handler by index
     * Needed for invocation via @row-hovered in <b-table>
     * @param index
     * @param event
     */
    rowHoverHandler (index, event) {
      this.locationHandlers[index].mouseover(event)
    },
    /**
     * Invoke appropriate row unhover handler by index
     * Needed for invocation via @row-unhovered in <b-table>
     * @param index
     * @param event
     */
    rowUnhoverHandler (index, event) {
      this.locationHandlers[index].mouseleave(event)
    }
  },
  computed: {
    /**
     * Expose imported gmapApi symbol as 'google' so we can use 'google.maps' object inside Vue as we would normally in
     * any other Javascript project.
     * (As recommended by the vue-google-maps documentation)
     */
    google: gmapApi
  },
  watch: {
    showVehiclePath (newValue) {
      if (newValue === true) {
        this.vehiclePath.setMap(this.map)
      } else {
        this.vehiclePath.setMap(null)
      }
    },
    showGpsMarkers (newValue) {
      const map = newValue === true ? this.map : null
      forEach(this.gpsMarkers, marker => {
        marker.setMap(map)
      })
    }
  },
  mounted () {
    // Initialize the google maps state once the map promise is resolved.
    this.$refs.map.$mapPromise.then((map) => {
      this.map = map
      this.initializeMap()
    })
  }
}
</script>

<style scoped lang="scss">
  .map-locations-container {
    width: 300px;
  }
  .map-container {
    width: 100%;
  }
  .path-disclaimer {
    font-style: italic;
    font-size: smaller;
  }
  .route-plan-map {
    height: 400px;
  }
  /* Adjust the info window header sizes in google map a wee bit down */
  .route-plan-map ::v-deep(.gm-style-iw-d) h5 {
    font-size: 1rem;
  }
  .route-plan-map ::v-deep(.gm-style-iw-d) h6 {
    font-size: .75rem;
  }
</style>
